import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Star, 
  Calendar, 
  Clock, 
  MapPin, 
  Phone, 
  Mail,
  Award,
  GraduationCap,
  Heart,
  Activity,
  Bone,
  Shield,
  Search,
  Filter,
  Users,
  CheckCircle,
  Stethoscope
} from 'lucide-react';
import { Card, Button, Badge, Input } from '../../components/ui';
import { mockDoctors } from '../../data/mockData';
import { searchFilter } from '../../utils';

const DoctorsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('all');

  // Get unique specialties
  const specialties = [
    'all',
    ...Array.from(new Set(mockDoctors.map(doctor => doctor.specialty)))
  ];

  // Filter doctors based on search and specialty
  const filteredDoctors = searchFilter(
    selectedSpecialty === 'all' 
      ? mockDoctors 
      : mockDoctors.filter(doctor => doctor.specialty === selectedSpecialty),
    searchTerm,
    ['name', 'specialty', 'bio']
  );

  const getSpecialtyIcon = (specialty: string) => {
    const iconMap: { [key: string]: any } = {
      'Cardiology': Heart,
      'Orthopedics': Bone,
      'Dermatology': Shield,
      'Internal Medicine': Activity,
    };
    return iconMap[specialty] || Heart;
  };

  const getDayName = (dayIndex: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayIndex];
  };

  const features = [
    {
      icon: Award,
      title: 'Board Certified',
      description: 'All our physicians are board-certified in their specialties'
    },
    {
      icon: GraduationCap,
      title: 'Continuing Education',
      description: 'Ongoing training and education to stay current with medical advances'
    },
    {
      icon: Users,
      title: 'Patient-Centered',
      description: 'Focused on providing personalized care for every patient'
    },
    {
      icon: CheckCircle,
      title: 'Quality Care',
      description: 'Committed to delivering the highest quality medical care'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-800 via-blue-900 to-blue-800 text-white overflow-hidden">
        {/* Medical Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=1920&h=1080&fit=crop&crop=center"
            alt="Our Medical Team"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800/60 via-blue-900/70 to-blue-800/65"></div>
        </div>
        
        {/* Decorative background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full opacity-10 blur-3xl transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500 rounded-full opacity-10 blur-3xl transform -translate-x-32 translate-y-32"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center space-y-8">
            {/* Doctors Badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-sm font-medium text-white/90">
                Expert Medical Team
              </span>
            </div>
            
            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                Meet Our{' '}
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  DOCTORS
                </span>
            </h1>
              <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                Our board-certified physicians and specialists are dedicated to providing exceptional medical care with compassion and expertise.
            </p>
            </div>
            
            {/* CTA Button */}
            <div className="pt-6">
            <Link to="/booking">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-3 rounded-full shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                <Calendar className="w-5 h-5 mr-2" />
                Book Consultation
              </Button>
            </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-4">
                  <feature.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Doctors Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Filter */}
          <div className="mb-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Medical Professionals
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Get to know our experienced doctors and find the right specialist for your healthcare needs.
              </p>
            </div>

            {/* Search Bar */}
            <div className="max-w-md mx-auto mb-8">
              <Input
                placeholder="Search doctors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<Search className="w-5 h-5" />}
              />
            </div>

            {/* Specialty Filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {specialties.map((specialty) => (
                <button
                  key={specialty}
                  onClick={() => setSelectedSpecialty(specialty)}
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedSpecialty === specialty
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  {specialty === 'all' ? 'All Specialties' : specialty}
                </button>
              ))}
            </div>
          </div>

          {/* Doctors Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredDoctors.map((doctor) => {
              const SpecialtyIcon = getSpecialtyIcon(doctor.specialty);
              return (
                <Card key={doctor.id} className="card-hover h-full" padding="none">
                  <div className="relative">
                    <img
                      src={doctor.photo}
                      alt={doctor.name}
                      className="w-full h-64 object-cover"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge 
                        variant={doctor.isAvailable ? 'success' : 'default'}
                        size="sm"
                      >
                        {doctor.isAvailable ? 'Available' : 'Busy'}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{doctor.name}</h3>
                      <div className="flex items-center space-x-2 mb-2">
                        <SpecialtyIcon className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-600 font-medium">{doctor.specialty}</span>
                      </div>
                      
                      {/* Rating */}
                      <div className="flex items-center space-x-2 mb-3">
                        <div className="flex items-center space-x-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < Math.floor(doctor.rating) 
                                  ? 'text-yellow-400 fill-current' 
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-gray-600">
                          {doctor.rating} ({doctor.reviewCount} reviews)
                        </span>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {doctor.bio}
                    </p>

                    {/* Qualifications */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Qualifications:</h4>
                      <div className="flex flex-wrap gap-1">
                        {doctor.qualifications.map((qual, index) => (
                          <Badge key={index} variant="info" size="sm">
                            {qual}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Schedule Preview */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">This Week:</h4>
                      <div className="space-y-1">
                        {doctor.schedule.filter(s => s.isWorking).slice(0, 3).map((schedule, index) => (
                          <div key={index} className="flex items-center justify-between text-xs text-gray-600">
                            <span>{getDayName(schedule.dayOfWeek)}</span>
                            <span>{schedule.startTime} - {schedule.endTime}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3">
                      <Link to={`/booking?doctor=${doctor.id}`} className="block">
                        <Button className="w-full bg-green-600 hover:bg-green-700">
                          <Calendar className="w-4 h-4 mr-2" />
                          Book Appointment
                        </Button>
                      </Link>
                      <Button variant="outline" className="w-full">
                        View Full Profile
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* No results message */}
          {filteredDoctors.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No doctors found</h3>
              <p className="text-gray-600">
                Try adjusting your search or filter criteria to find the doctors you're looking for.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Our Doctors Section */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Why Choose Our Medical Team?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our physicians are not just highly qualified medical professionals, 
                but also compassionate caregivers who put your health and well-being first.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Award className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Board Certification</h3>
                    <p className="text-gray-600 text-sm">
                      All our doctors are board-certified and maintain active licenses in their specialties.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Continuous Learning</h3>
                    <p className="text-gray-600 text-sm">
                      Regular training and education to stay current with the latest medical advances.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Patient-Centered Care</h3>
                    <p className="text-gray-600 text-sm">
                      We believe in treating the whole person, not just the condition.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <Card className="p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Need Help Finding the Right Doctor?
                </h3>
                <p className="text-gray-600 mb-6">
                  Our patient coordinators can help you find the right specialist 
                  for your specific health needs and schedule your appointment.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span>Call us: (555) 123-4567</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span>Email: info@healthcareplus.com</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>Mon-Fri: 8AM-6PM</span>
                  </div>
                </div>
                <div className="mt-6 space-y-3">
                  <Link to="/contact">
                    <Button className="w-full">
                      Contact Our Team
                    </Button>
                  </Link>
                  <Link to="/booking">
                    <Button variant="outline" className="w-full">
                      Book Appointment
                    </Button>
                  </Link>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-gradient-to-br from-slate-800 via-blue-900 to-blue-800 text-white overflow-hidden">
        {/* Medical Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=1920&h=1080&fit=crop&crop=center"
            alt="Medical Team"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800/80 via-blue-900/85 to-blue-800/80"></div>
        </div>

        {/* Decorative background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500 rounded-full opacity-10 blur-3xl transform -translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full opacity-10 blur-3xl transform translate-x-32 translate-y-32"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="space-y-6">
            {/* Section Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-sm font-medium text-white/90">
            Ready to Meet Your Doctor?
              </span>
            </div>

            {/* Main Heading */}
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
                Expert Care from{' '}
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  Trusted Physicians
                </span>
          </h2>
              <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                Take the first step towards better health. Book a consultation with one of our expert physicians today.
          </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
            <Link to="/booking">
                <Button size="lg" className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-3 rounded-full shadow-xl transform hover:scale-105 transition-all duration-300">
                <Calendar className="w-5 h-5 mr-2" />
                Book Consultation
              </Button>
            </Link>
            <Link to="/services">
                <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-slate-800 backdrop-blur-sm font-semibold px-8 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all duration-300">
                  <Stethoscope className="w-5 h-5 mr-2" />
                View Our Services
              </Button>
            </Link>
            </div>

            {/* Additional Info */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-8 pt-8">
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                <span className="text-sm font-medium">Board-Certified Physicians</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                <span className="text-sm font-medium">Specialized Expertise</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                <span className="text-sm font-medium">Personalized Treatment</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DoctorsPage;